import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';

const AuthContext = createContext(null);

// Setup axios interceptor to always include token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.withCredentials = true;
  return config;
});

// Response interceptor: auto-refresh access token on 401 using the refresh
// cookie. Keeps the session alive transparently as long as the refresh token
// (httpOnly cookie, 7 or 30 days) is still valid.
let isRefreshing = false;
let refreshSubscribers = [];
const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
};
// Portál-oldalak (dolgozó / vendég / ügyfél / nyilvános): itt NINCS tulajdonosi session → a 401-et nem szabad
// refresh-sel kezelni, különben „No refresh token” hibával a /login-ra dobná a dolgozót (iter 318 javítás).
const PORTAL_PATHS = ['/me', '/dolgozo', '/w/', '/g/', '/c/', '/portal/', '/p/', '/pay/', '/admin'];
const PORTAL_APIS = ['/api/wacc', '/api/wportal', '/api/gportal', '/api/cportal', '/api/passkey/', '/api/portal/', '/api/public/', '/api/proposals/public', '/api/orders/public'];
const isPortalContext = (url) => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  return PORTAL_PATHS.some((p) => path === p || path.startsWith(p)) || PORTAL_APIS.some((a) => (url || '').includes(a));
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (
      error.response &&
      error.response.status === 401 &&
      original &&
      !original._retry &&
      !isPortalContext(original.url) &&
      !original.url?.includes('/api/auth/login') &&
      !original.url?.includes('/api/auth/register') &&
      !original.url?.includes('/api/auth/refresh') &&
      !original.url?.includes('/api/auth/me') &&
      !original.url?.includes('/api/admin/')
    ) {
      original._retry = true;
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push((newToken) => {
            if (newToken) original.headers.Authorization = `Bearer ${newToken}`;
            resolve(axios(original));
          });
        });
      }
      isRefreshing = true;
      try {
        const r = await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        const newToken = r?.data?.token;
        if (newToken) {
          localStorage.setItem('access_token', newToken);
          original.headers.Authorization = `Bearer ${newToken}`;
        }
        onRefreshed(newToken);
        return axios(original);
      } catch (refreshErr) {
        onRefreshed(null);
        localStorage.removeItem('access_token');
        // Soft redirect to /login if we are inside the SPA
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.assign('/login');
        }
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // Pre-emptive token refresh — checks JWT exp claim every 5 minutes and ONLY
  // refreshes when the token is within 5 minutes of expiry. Avoids unnecessary
  // 401-noise in the Network panel when the access token is still fresh.
  useEffect(() => {
    if (!user) return;
    const decodeExp = (token) => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return Number(payload.exp) * 1000; // ms
      } catch { return 0; }
    };
    const refreshIfNeeded = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      const expMs = decodeExp(token);
      // Only refresh if token expires within the next 5 minutes
      if (expMs && expMs - Date.now() > 5 * 60 * 1000) return;
      try {
        const r = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
          withCredentials: true,
          validateStatus: (s) => s < 500,
        });
        if (r?.status === 200 && r?.data?.token) {
          localStorage.setItem('access_token', r.data.token);
        }
      } catch (_) { /* silent — interceptor handles hard failures */ }
    };
    // Quick check on mount, then every 5 minutes
    refreshIfNeeded();
    const iv = setInterval(refreshIfNeeded, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [user]);

  const checkAuth = async () => {
    try {
      // iter 208 — Preemptive guard: if no access_token in localStorage AND no
      // refresh cookie hint, the user is anonymous → skip the /me + /refresh
      // chain that otherwise generates 3× 401 noise per page load.
      const hasAccessToken = !!localStorage.getItem('access_token');
      // We can't read httpOnly refresh cookie, but if we've never logged in this
      // browser there is no point in pinging /me — leave user as null.
      // The login() / register() flows set hasLoggedInOnce.
      const hasLoggedInOnce = localStorage.getItem('acountix_logged_in') === '1';
      if (!hasAccessToken && !hasLoggedInOnce) {
        setUser(null);
        setLoading(false);
        return;
      }

      // First try /me — this validates an existing access_token
      let response = await axios.get(`${API_URL}/api/auth/me`, {
        validateStatus: (s) => s < 500,
      });

      // If access token expired (401), try to refresh using the long-lived refresh cookie
      if (response.status === 401) {
        try {
          const rr = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
            withCredentials: true,
            validateStatus: (s) => s < 500,
          });
          if (rr.status === 401) {
            // refresh cookie also expired → permanent logout, stop the loop
            localStorage.removeItem('access_token');
            localStorage.removeItem('acountix_logged_in');
            setUser(null);
            return;
          }
          // Retry /me with the freshly minted access cookie
          response = await axios.get(`${API_URL}/api/auth/me`, {
            validateStatus: (s) => s < 500,
          });
        } catch (_) {
          // refresh failed → user is logged out for real
        }
      }

      if (response.status === 200) {
        setUser(response.data);
      } else {
        setUser(null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('acountix_logged_in');
      }
    } catch (error) {
      setUser(null);
      localStorage.removeItem('access_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, remember = false) => {
    const response = await axios.post(
      `${API_URL}/api/auth/login`,
      { email, password, remember }
    );
    if (response.data.token) {
      localStorage.setItem('access_token', response.data.token);
    }
    localStorage.setItem('acountix_logged_in', '1'); // iter 208 — bootstrap hint
    setUser(response.data);
    return response.data;
  };

  const register = async (name, email, password, role = 'accountant') => {
    const response = await axios.post(
      `${API_URL}/api/auth/register`,
      { name, email, password, role }
    );
    if (response.data.token) {
      localStorage.setItem('access_token', response.data.token);
    }
    localStorage.setItem('acountix_logged_in', '1'); // iter 208 — bootstrap hint
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API_URL}/api/auth/logout`, {});
    localStorage.removeItem('access_token');
    localStorage.removeItem('acountix_logged_in');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Convenience hook — iter 187. Returns true if the current user has the admin
// role (NOT the separate hidden /admin panel auth — that uses cookies).
// Use this to gate any admin-only background polling so regular users don't
// fire 401-emitting requests they have no business firing.
export function useIsAdmin() {
  const { user } = useAuth();
  return user?.role === 'admin';
}

